import {Component, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef} from '@angular/core';
import {MatSidenav} from '@angular/material/sidenav';
import {Router, NavigationEnd, ActivatedRoute} from '@angular/router';
import {User} from '@core/models/user.model';
import {GraphqlClientService} from '@core/services/graphql-client.service';
import {AuthState} from '@core/store/auth/auth.state';
import { FetchOrgUserPhoto, GetOrgUserById, LoadCountryById, LoadOrgById, ResetSharedState } from '@core/store/shared.actions';
import { Logout } from '@core/store/auth/auth.actions';
import {SharedState} from '@core/store/shared.state';
import {environment} from '@env/environment';
import {Actions, ofActionCompleted, Select, Store} from '@ngxs/store';
import {CookieService} from 'ngx-cookie-service';
import {Observable, BehaviorSubject} from 'rxjs';
import { filter, mergeMap, take, tap } from 'rxjs/operators';
import { NavigationConfig } from './modules/organization/models/organization';
import { OrgUser } from './modules/organization/models/org-user';
import { APP_ROUTING } from '@configs/constants';
import { CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';

/** Where each option of the schedule module chooser leads. */
const SCHEDULE_MODULE_URLS = {
  classic: APP_ROUTING.schedule.main,
  platform: '/schedule-v2'
};

/** Space between the sidebar's edge and the schedule module chooser. */
const SCHEDULE_CHOOSER_GAP = 14;

@Component({
    selector: 'hmt-app-wrapper-v2',
    templateUrl: './app-wrapper-v2.component.html',
    styleUrls: ['./app-wrapper-v2.component.scss'],
    standalone: false
})
export class AppWrapperV2Component implements OnInit, AfterViewInit {

  @Select(AuthState.isAuthenticated) isAuthenticated: Observable<boolean>;
  @Select(AuthState.getUser) user: Observable<User>;
  @Select(SharedState.getSpinnerStatus) isSpinnerEnabled: Observable<{spinner: boolean; spinnerMessage: string}>;
  @Select(SharedState.getNavigationConfig) navigationConfig: Observable<NavigationConfig[]>;
  @Select(SharedState.getOrgUser) orgUser$: Observable<OrgUser>;
  @Select(SharedState.getOrgUserPhoto) orgUserPhoto$: Observable<string>;
  @ViewChild('sidenav') sidenav: MatSidenav;
  public orgUserPhotoUrl$: Observable<string>;
  title = 'Haulmatic';
  expanded = true;
  isShowing = false;
  developer = environment.developer;
  version = environment.version;
  userOrganizationId: string;
  isLoading = false;
  activeModuleTitle: string = null;
  isNotificationsEnabled = false;
  isSettingsEnabled = false;
  isSupportEnabled = false;
  profilePicture: string;
  orgUserPhotoId: string;
  orgUserPhotoUrl: string;
  orgUser: OrgUser;
  orgUserId: string;
  navBarModuleConfigs$: Observable<NavigationConfig[]>;

  scheduleChooserOpen = false;
  scheduleChooserOrigin: CdkOverlayOrigin;
  /**
   * Beside the nav item (arrow points back at it); falls back below/above when there is no room.
   * offsetX is recalculated on open so the gap is measured from the sidebar's edge, which differs
   * from the nav item's edge between the expanded and collapsed sidebar.
   */
  scheduleChooserPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: SCHEDULE_CHOOSER_GAP, panelClass: 'schedule-chooser-beside' },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 }
  ];

  private navBarModuleConfigsSubject = new BehaviorSubject<NavigationConfig[]>([]);

  constructor(private store: Store,
              private router: Router,
              private cookiesService: CookieService,
              private gqlService: GraphqlClientService,
              private activatedRoute: ActivatedRoute,
              private cdr: ChangeDetectorRef,
              private actions$: Actions
  ) {
    this.userOrganizationId = this.cookiesService.get('orgId');
    this.gqlService.createLinkWithGQLServer();

    this.user
      .pipe(
        filter(u => u != null),
        take(1),
        mergeMap(user => {
          this.orgUserId = user.orgUserId;

          if (!user.orgUserId) {
            return this.store.dispatch(new GetOrgUserById(user.orgId, user.orgUserId));
          }

          this.store.dispatch(new GetOrgUserById(user.orgId, user.orgUserId));
          return this.store.dispatch(new LoadOrgById(user.orgId));
        }),
        mergeMap(_ => this.store.dispatch(new LoadCountryById())),
      )
      .subscribe();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe(() => {
      this.updateActiveModule(this.router.url);
    });
  }

  /** Control Tower hosts its own Supply Chain Brain dock, so hide the global AVA bubble there. */
  get isControlTowerRoute(): boolean {
    return this.router.url.includes('control-tower');
  }

  ngOnInit(): void {
    this.navBarModuleConfigs$ = this.navBarModuleConfigsSubject.asObservable();

    this.orgUserPhotoUrl$ = this.orgUserPhoto$.pipe(
      tap(photo => {
      })
    );

    const savedState = localStorage.getItem('sidenavExpanded');
    if (savedState !== null) {
      this.expanded = savedState === 'true';
    } else {
      this.expanded = true;
    }

    setTimeout(() => {
      if (this.sidenav) {
        this.sidenav.opened = true;
      }
    }, 100);

    this.navigationConfig.subscribe(navItems => {
      this.navBarModuleConfigsSubject.next(navItems);
    });

    this.updateActiveModule(this.router.url);

    this.actions$
      .pipe(
        ofActionCompleted(Logout),
        take(1),
        tap(action => {
          this.router.navigate([APP_ROUTING.auth.login], {queryParams: {redirect: action.action.currentPage}});
        })
      ).subscribe();

    this.orgUser$.pipe(
      filter(orgUser => orgUser != null && !Array.isArray(orgUser) && typeof orgUser === 'object'),
      tap(orgUser => {
        this.orgUser = orgUser;
        this.orgUserPhotoId = orgUser.photo;
        if (orgUser.photo) {
          this.store.dispatch(new FetchOrgUserPhoto(orgUser.photo));
        } else {
          console.warn('No photo ID found in orgUser');
        }
      })
    ).subscribe();

    this.orgUserPhoto$.pipe(
      filter(photo => photo != null && photo.length > 0),
      tap(photo => {
        this.orgUserPhotoUrl = photo;
        this.cdr.detectChanges();
      })
    ).subscribe();

  }


  mouseenter() {
    this.isShowing = true;
  }

  mouseleave() {
    if (!this.expanded) {
      this.isShowing = false;
    }
  }

  onToggle() {
    this.expanded = !this.expanded;
    localStorage.setItem('sidenavExpanded', String(this.expanded));
  }

  collapseModule(moduleConfig: NavigationConfig, origin?: CdkOverlayOrigin): void {
    if (moduleConfig.disabled) {
      return;
    }

    // Schedule has two modules (Classic TMS and TMS Platform); let the user pick one instead of navigating.
    if (origin && this.isScheduleModule(moduleConfig)) {
      this.scheduleChooserOrigin = origin;
      if (!this.scheduleChooserOpen) {
        this.alignScheduleChooserToSidebar(origin);
      }
      this.scheduleChooserOpen = !this.scheduleChooserOpen;
      return;
    }

    this.activeModuleTitle = moduleConfig.title;

    this.router.navigate([moduleConfig.navigationUrl]);
  }

  isScheduleModule(moduleConfig: NavigationConfig): boolean {
    const url = (moduleConfig?.navigationUrl ?? '').split('?')[0].replace(/\/+$/, '');
    return url === APP_ROUTING.schedule.main || url === APP_ROUTING.schedule.main.slice(1) || moduleConfig?.title === 'Schedule';
  }

  openScheduleModule(module: keyof typeof SCHEDULE_MODULE_URLS): void {
    this.closeScheduleChooser();
    this.activeModuleTitle = 'Schedule';
    this.router.navigate([SCHEDULE_MODULE_URLS[module]]);
  }

  private alignScheduleChooserToSidebar(origin: CdkOverlayOrigin): void {
    const item = origin.elementRef.nativeElement as HTMLElement;
    const itemRight = item.getBoundingClientRect().right;
    const sidebarRight = item.closest('.main-sidenav')?.getBoundingClientRect().right ?? itemRight;
    const [beside, ...fallbacks] = this.scheduleChooserPositions;
    this.scheduleChooserPositions = [{ ...beside, offsetX: sidebarRight - itemRight + SCHEDULE_CHOOSER_GAP }, ...fallbacks];
  }

  closeScheduleChooser(): void {
    this.scheduleChooserOpen = false;
  }

  onScheduleChooserKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeScheduleChooser();
    }
  }

  navigateTo(subPageConfig: any): void {
    event?.stopPropagation();

    if (subPageConfig.parentTitle) {
      this.activeModuleTitle = subPageConfig.parentTitle;
    }

    if (subPageConfig.withinApp) {
      this.router.navigate([subPageConfig.path]);
    } else {
      window.open(subPageConfig.path, '_blank');
    }
  }

  ngAfterViewInit() {
    if (this.sidenav) {
      this.sidenav.opened = true;
    }
  }

  onMenuOpened() {
    setTimeout(() => {
      const focusedElement = document.activeElement as HTMLElement;
      if (focusedElement && focusedElement.classList.contains('mat-menu-item')) {
        focusedElement.blur();
      }
    }, 0);
  }

  onLogout() {
    this.store.dispatch([new Logout(false), new ResetSharedState()]);
  }

  private updateActiveMenuItem(url: string): void {
    this.updateActiveModule(url);
  }

  private updateActiveModule(url: string) {
    this.activeModuleTitle = null;
    if (url.includes('/dashboard')) {
      this.activeModuleTitle = 'Dashboard';
    } else if (url.includes('/schedule')) {
      this.activeModuleTitle = 'Schedule';
    } else if (url.includes('/reports/job-master')) {
      this.activeModuleTitle = 'Job Master';
    } else if (url.includes('/invoice-management')) {
      this.activeModuleTitle = 'Invoice Management';
    } else if (url.includes('/aggregated-jpm')) {
      this.activeModuleTitle = 'Control Tower';
    } else if (url.includes('/order-management')) {
      this.activeModuleTitle = 'Order Management';
    } else if (url.includes('/contract-management')) {
      this.activeModuleTitle = 'Contract Management';
    } else if (url.includes('/task-feed')) {
      this.activeModuleTitle = 'Tasks';
    } else if (url.includes('/fuel')) {
      this.activeModuleTitle = 'Fuel';
    } else if (url.includes('/organizations-manager')) {
      this.activeModuleTitle = 'Organizations Manager';
    } else if (url.includes('/reports')) {
      this.activeModuleTitle = 'Reports';
    }
  }
}


